interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  publishedAt: string;
  category: string;
  tags: string[];
  featured: boolean;
}

export function renderBlog(): string {
  return /* html */`
<div class="page-wrapper blog-page">
  <div class="page-hero">
    <span class="section-label">Devlog</span>
    <h1 class="hero-title anim-fade-up">
      Notes from<br/><span class="gradient-text">the lab.</span>
    </h1>
    <p class="hero-mission anim-fade-up" style="animation-delay:0.1s">
      Release notes, technical deep dives, project updates, and company thinking.
      No SEO filler. No thought leadership. Just actual things we've built or learned.
    </p>
  </div>

  <div class="page-section">
    <div id="blog-posts" class="blog-posts stagger">
      <!-- Populated by initBlog() -->
    </div>
  </div>
</div>
`;
}

export function initBlog(): void {
  import('../data/posts.json').then(({ default: raw }) => {
    const posts = (raw as Post[]).sort(
      (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
    );
    const container = document.getElementById('blog-posts');
    if (!container) return;

    container.innerHTML = posts.map((post, i) => postCard(post, i === 0)).join('');

    container.querySelectorAll('.post-card').forEach(card => {
      card.addEventListener('click', () => {
        const id = (card as HTMLElement).dataset.postId;
        openPost(posts.find(p => p.id === id)!);
      });
    });
  });
}

function postCard(post: Post, large = false): string {
  const date = new Date(post.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const catColor: Record<string, string> = {
    'Company': 'teal', 'Release': 'green', 'Technical': 'purple', 'Project': 'amber',
  };

  return /* html */`
  <article class="post-card card ${large ? 'post-card--large' : ''}" data-post-id="${post.id}"
           tabindex="0" role="button" aria-label="Read: ${post.title}">
    <div class="post-meta">
      <span class="badge badge-${catColor[post.category] ?? 'dim'}">${post.category}</span>
      <time class="post-date" datetime="${post.publishedAt}">${date}</time>
    </div>
    <h2 class="post-title">${post.title}</h2>
    <p class="post-excerpt">${post.excerpt}</p>
    <div class="post-tags">
      ${post.tags.slice(0, 4).map(t => `<span class="badge badge-dim">${t}</span>`).join('')}
    </div>
    <div class="post-author">— ${post.author}</div>
  </article>
  `;
}

function openPost(post: Post): void {
  const date = new Date(post.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const modal = document.createElement('div');
  modal.className = 'modal-overlay';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.innerHTML = /* html */`
  <div class="modal-content post-detail">
    <button class="modal-close" aria-label="Close">×</button>
    <div class="post-detail-header">
      <div class="post-meta">
        <span class="badge badge-teal">${post.category}</span>
        <time datetime="${post.publishedAt}">${date}</time>
      </div>
      <h1 class="post-detail-title">${post.title}</h1>
      <div class="post-author">— ${post.author}</div>
    </div>
    <div class="post-body">${markdownToHtml(post.content)}</div>
    <div class="post-tags" style="margin-top:var(--s6)">
      ${post.tags.map(t => `<span class="badge badge-teal">${t}</span>`).join('')}
    </div>
  </div>
  `;

  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
  modal.querySelector('.modal-close')!.addEventListener('click', () => modal.remove());
  document.body.appendChild(modal);
  (modal.querySelector('.modal-close') as HTMLElement).focus();
}

// Minimal markdown → HTML (headings, bold, inline code, paragraphs, code blocks)
function markdownToHtml(md: string): string {
  return md
    .split('\n\n')
    .map(block => {
      if (block.startsWith('```')) {
        const code = block.replace(/```[a-z]*\n?/, '').replace(/```$/, '');
        return `<pre class="code-block"><code>${escapeHtml(code.trim())}</code></pre>`;
      }
      let html = block
        .replace(/### (.+)/g, '<h3>$1</h3>')
        .replace(/## (.+)/g,  '<h2>$1</h2>')
        .replace(/# (.+)/g,   '<h1>$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.+?)`/g, '<code>$1</code>')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
      if (!html.startsWith('<h')) html = `<p>${html}</p>`;
      return html;
    })
    .join('\n');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}
